import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { levelForXp, XP_CAP } from "@/lib/xp";

type CashableItem = {
  id: string;
  title: string;
  xp_value: number;
  project_tag: string | null;
};

type CashInData = {
  items: CashableItem[];
  total_xp: number;
};

export function useCashIn() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery<CashInData>({
    queryKey: ["cash-in"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke(
        "check-hp-completions",
        { body: { user_id: user!.id } }
      );
      if (error) throw error;
      return data as CashInData;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 min cache
    refetchOnWindowFocus: false,
  });

  const cashIn = useMutation({
    mutationFn: async (itemIds: string[]) => {
      if (!user) throw new Error("Not authenticated");

      // 2026-05-17 evening MDT: the entire cash-in flow now goes through the
      // `cash-in-items` edge function (v12). Pre-fix this was three separate
      // client-side writes — items.status, user_stats read, user_stats write —
      // and it (a) never set items.cashed_in_at, so the same items came back
      // in the cash-in queue forever; (b) had a TOCTOU race on user_stats.xp
      // because two parallel cash-ins could read the same xp and both write
      // their sum; (c) used `level = levelForXp(xp)` client-side, so a stale
      // tab could clobber a fresh one's level. Edge fn fixes all three: it
      // does an atomic UPDATE...RETURNING on items (gates on cashed_in_at IS
      // NULL so concurrent callers can't double-claim), then calls the
      // public.award_xp SECURITY DEFINER fn under a row lock which derives
      // the new level via public.level_for_xp inside the same lock window.
      //
      // We still hand-roll the leveled_up flag client-side because only the
      // browser knows what level the user was seeing immediately before the
      // mutation (server can't tell what stale data the tab was showing).
      const { data, error } = await supabase.functions.invoke<{
        success?: boolean;
        count?: number;
        total_xp?: number;
        new_xp?: number;
        new_level?: number;
        new_streak?: number;
        new_daily_completed?: number;
        new_total_completed?: number;
        error?: string;
      }>("cash-in-items", {
        body: { item_ids: itemIds },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const count = data?.count ?? 0;
      const totalXp = data?.total_xp ?? 0;
      const newLevel = data?.new_level ?? 0;
      const newXp = data?.new_xp ?? 0;
      // Compute what the prior level was so we can show a "level up"
      // celebration if cash-in crossed a threshold. Math.max guards against
      // edge cases when newXp < totalXp (which shouldn't happen, but defends
      // against a stale RPC response).
      const priorXp = Math.max(0, Math.min(newXp - totalXp, XP_CAP));
      const priorLevel = levelForXp(priorXp);

      return {
        count,
        xpAwarded: totalXp,
        leveledUp: newLevel > priorLevel,
      };
    },
    onSuccess: () => {
      // Set cache to empty immediately with a fresh timestamp so it won't
      // refetch for another staleTime (5 min). Do NOT call invalidateQueries
      // on ["cash-in"] — that triggers an immediate background refetch that
      // races the DB update and can overwrite this with the old item list.
      queryClient.setQueryData(["cash-in"], { items: [], total_xp: 0 });
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["item-stats"] });
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
      queryClient.invalidateQueries({ queryKey: ["done-this-week"] });
    },
  });

  return {
    items: query.data?.items || [],
    totalXp: query.data?.total_xp || 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    cashIn,
  };
}
